export interface DashboardProgress {
  foundationProgress: number;
  externalAssessmentProgress: number;
  internalAssessmentProgress: number;
  formulationProgress: number;
  executionProgress: number;
}

export const DASHBOARD_STAGES = [
  {
    key: 'foundationProgress',
    title: 'Foundation',
    description: 'Set your vision, mission, values and objectives.',
    path: 'strategy/foundation',
  },
  {
    key: 'externalAssessmentProgress',
    title: 'External analysis',
    description: 'Explore your market, industry and competitors.',
    path: 'strategy/external-assessment',
  },
  {
    key: 'internalAssessmentProgress',
    title: 'Internal analysis',
    description: 'Understand your strengths, resources and capabilities.',
    path: 'strategy/internal-assessment',
  },
  {
    key: 'formulationProgress',
    title: 'Strategy formulation',
    description: 'Choose your priorities and build your strategic plan.',
    path: 'strategy/formulation',
  },
  {
    key: 'executionProgress',
    title: 'Execution',
    description: 'Turn your plan into projects, tasks and progress.',
    path: 'execution',
  },
] as const satisfies readonly {
  key: keyof DashboardProgress;
  title: string;
  description: string;
  path: string;
}[];

const planningStages = DASHBOARD_STAGES.slice(0, 4);

function clampProgress(value: number): number {
  return Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : 0;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

// Match the company overview's four foundation completion checks using saved content.
export function getFoundationProgress(foundationData: unknown): number {
  const foundation = asRecord(foundationData);
  const vision = asRecord(foundation.vision);
  const mission = asRecord(foundation.mission);
  const values = asRecord(foundation.values);
  const objectives = asRecord(foundation.objectives);

  const hasStatement = (entries: Record<string, unknown>, keys: string[]) =>
    keys.some((key) => {
      const entry = entries[key];
      return typeof entry === 'string' && entry.trim().length > 20;
    });

  const valuesCount = ['values1', 'values2', 'values3'].reduce((total, key) => {
    const value = values[key];
    if (Array.isArray(value)) return total + value.length;
    if (typeof value !== 'string' || !value.trim()) return total;
    try {
      const parsed: unknown = JSON.parse(value);
      return total + (Array.isArray(parsed) ? parsed.length : 0);
    } catch {
      return total + value.split(',').filter((entry) => entry.trim().length > 0).length;
    }
  }, 0);

  const hasObjective = ['financial', 'customer', 'process', 'talent'].some((key) => {
    const entries = objectives[key];
    return Array.isArray(entries) && entries.length > 0;
  });
  const completed = [
    hasStatement(vision, ['vision1', 'vision2', 'vision3']),
    hasStatement(mission, ['mission1', 'mission2', 'mission3']),
    valuesCount >= 5,
    hasObjective,
  ].filter(Boolean).length;

  return completed * 25;
}

export function getPlanningProgress(progress: DashboardProgress): number {
  const values = planningStages.map((stage) => clampProgress(progress[stage.key]));
  if (values.every((value) => value === 100)) return 100;

  const average = values.reduce((total, value) => total + value, 0) / values.length;
  // Rounding a nearly finished plan must not label an incomplete stage as complete.
  return Math.min(99, Math.round(average));
}

export function getNextStage(progress: DashboardProgress): (typeof DASHBOARD_STAGES)[number] {
  return (
    planningStages.find((stage) => clampProgress(progress[stage.key]) < 100) ?? DASHBOARD_STAGES[4]
  );
}

export function getPlanningStatus(
  progress: number
): 'Not started' | 'In progress' | 'Plan complete' {
  const value = clampProgress(progress);
  if (value === 100) return 'Plan complete';
  return value === 0 ? 'Not started' : 'In progress';
}

export type DashboardCompanySort = 'newest' | 'name' | 'progress';

interface SortableCompany {
  name: string;
  createdAt: string;
  strategyProgress: number | null;
}

function compareNames(a: SortableCompany, b: SortableCompany): number {
  return (
    a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }) ||
    a.name.localeCompare(b.name, 'en')
  );
}

function creationTime(company: SortableCompany): number {
  const timestamp = Date.parse(company.createdAt);
  return Number.isFinite(timestamp) ? timestamp : 0;
}

export function filterAndSortCompanies<T extends SortableCompany>(
  companies: readonly T[],
  query: string,
  sort: DashboardCompanySort
): T[] {
  const search = query.trim().toLowerCase();
  const results = companies.filter((company) => company.name.toLowerCase().includes(search));

  return results.sort((a, b) => {
    if (sort === 'newest') {
      return creationTime(b) - creationTime(a) || compareNames(a, b);
    }

    if (sort === 'progress') {
      const aKnown = a.strategyProgress !== null && Number.isFinite(a.strategyProgress);
      const bKnown = b.strategyProgress !== null && Number.isFinite(b.strategyProgress);
      if (aKnown !== bKnown) return aKnown ? -1 : 1;
      if (aKnown && bKnown) {
        return (
          clampProgress(b.strategyProgress!) - clampProgress(a.strategyProgress!) ||
          compareNames(a, b)
        );
      }
    }

    return compareNames(a, b);
  });
}
